import type { CustomRendererProps } from "streamdown"
import { WidgetTaskSummary } from "@/components/chat/WidgetTaskSummary"
import { WidgetTaskOngoing } from "@/components/chat/WidgetTaskOngoing"
import type { TaskStop } from "@/components/chat/TaskSummaryContent"
import {
  useWidgetTabs,
  type TaskDetail,
  type TaskTimelineStep,
} from "@/hooks/useWidgetTabs"

/**
 * Slick AI "generative widgets" — render inline with streamed prose. The
 * agent emits a fenced block tagged `slick-widget` whose body is JSON:
 *
 * ```slick-widget
 * { "type": "taskSummary", "taskRef": "ZL-2847", ... }
 * ```
 *
 * Streamdown calls this component for every such fence (see the
 * `plugins.renderers` wiring in AIChatAnswer). While the reply is still
 * streaming the JSON is partial — `isIncomplete` / a failed parse just
 * renders nothing until the fence closes.
 */
export const SLICK_WIDGET_LANGUAGE = "slick-widget"

const asStop = (value: unknown): TaskStop => {
  const v = (value ?? {}) as Record<string, unknown>
  return {
    countryCode: String(v.countryCode ?? ""),
    postalCode: String(v.postalCode ?? ""),
    city: v.city != null ? String(v.city) : undefined,
  }
}

const num = (value: unknown): number | undefined =>
  value != null ? Number(value) : undefined

const str = (value: unknown): string | undefined =>
  value != null ? String(value) : undefined

const bool = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined

const parseSteps = (value: unknown): TaskTimelineStep[] =>
  Array.isArray(value)
    ? (value as unknown[]).map((raw) => {
        const s = (raw ?? {}) as Record<string, unknown>
        return {
          label: String(s.label ?? ""),
          status:
            s.status === "done" || s.status === "current"
              ? s.status
              : "upcoming",
          detail: str(s.detail),
          tone: s.tone === "warn" ? "warn" : undefined,
        }
      })
    : []

/** No `url` on a document in the payload yet — until the agent side can
 * supply one, its title just isn't clickable. */
const parseDocuments = (value: unknown) =>
  Array.isArray(value)
    ? (value as unknown[]).map((raw) => {
        const d = (raw ?? {}) as Record<string, unknown>
        const url = str(d.url)
        return {
          fileName: String(d.fileName ?? ""),
          onOpen: url ? () => window.open(url, "_blank") : () => {},
        }
      })
    : undefined

const parseTaskDetailDocuments = (value: unknown) =>
  Array.isArray(value)
    ? (value as unknown[]).map((raw) => {
        const d = (raw ?? {}) as Record<string, unknown>
        return { fileName: String(d.fileName ?? ""), kind: str(d.kind) }
      })
    : undefined

const SlickWidgetRenderer = ({ code, isIncomplete }: CustomRendererProps) => {
  const { openTaskDetails } = useWidgetTabs()

  if (isIncomplete) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(code)
  } catch {
    return null
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
    return null
  const data = parsed as Record<string, unknown>

  const taskRef = String(data.taskRef ?? "")
  const steps = parseSteps(data.steps)

  switch (data.type) {
    case "taskSummary": {
      const pickup = asStop(data.pickup)
      const delivery = asStop(data.delivery)
      const durationMinutes = Number(data.durationMinutes ?? 0)
      const distanceKm = Number(data.distanceKm ?? 0)
      const tollsEur = Number(data.tollsEur ?? 0)
      const durationDeltaMinutes = num(data.durationDeltaMinutes)
      const distanceDeltaKm = num(data.distanceDeltaKm)
      const tollsDeltaEur = num(data.tollsDeltaEur)
      const note = str(data.note)
      const noteTone =
        data.noteTone === "positive" || data.noteTone === "warning"
          ? data.noteTone
          : undefined
      const dateLabel = str(data.dateLabel)

      const detail: TaskDetail = {
        taskStatus: "done",
        routeLabel: taskRef,
        dateLabel,
        documents: parseTaskDetailDocuments(data.documents),
        steps,
        summary: {
          pickup,
          delivery,
          durationMinutes,
          distanceKm,
          tollsEur,
          durationDeltaMinutes,
          distanceDeltaKm,
          tollsDeltaEur,
          note,
          noteTone,
        },
      }

      return (
        <WidgetTaskSummary
          taskRef={taskRef}
          dateLabel={dateLabel}
          pickup={pickup}
          delivery={delivery}
          durationMinutes={durationMinutes}
          distanceKm={distanceKm}
          tollsEur={tollsEur}
          durationDeltaMinutes={durationDeltaMinutes}
          distanceDeltaKm={distanceDeltaKm}
          tollsDeltaEur={tollsDeltaEur}
          documents={parseDocuments(data.documents)}
          note={note}
          noteTone={noteTone}
          onOpenInPanel={() => openTaskDetails(taskRef, taskRef, detail)}
        />
      )
    }
    case "taskOngoing": {
      const updateLabel = String(data.updateLabel ?? "")
      const scheduleDeltaMinutes = num(data.scheduleDeltaMinutes)

      const detail: TaskDetail = {
        taskStatus: "ongoing",
        routeLabel: taskRef,
        dateLabel: updateLabel,
        scheduleDeltaMinutes,
        documents: parseTaskDetailDocuments(data.documents),
        steps,
      }

      return (
        <WidgetTaskOngoing
          taskRef={taskRef}
          updateLabel={updateLabel}
          currentStage={String(data.currentStage ?? "")}
          offRoute={bool(data.offRoute)}
          scheduleDeltaMinutes={scheduleDeltaMinutes}
          nextStage={str(data.nextStage)}
          nextEta={str(data.nextEta)}
          progressPercent={Number(data.progressPercent ?? 0)}
          onOpenInPanel={() => openTaskDetails(taskRef, taskRef, detail)}
        />
      )
    }
    default:
      return null
  }
}

export default SlickWidgetRenderer
